import { memo, PropsWithChildren, useEffect, useRef, useState } from 'react'
import type { Stripe, StripeElementLocale } from '@stripe/stripe-js'
import { loadStripe } from '@stripe/stripe-js/pure'
import { Elements } from '@stripe/react-stripe-js'
import { AppManager } from '@app/managers'
import { STRIPE_KEY } from '@app/configs/app-config'
import { useRouter } from 'next/router'

const locales = [
  'auto',
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'en',
  'en-GB',
  'es',
  'es-419',
  'et',
  'fi',
  'fil',
  'fr',
  'fr-CA',
  'hr',
  'hu',
  'id',
  'it',
  'ja',
  'lt',
  'lv',
  'ms',
  'mt',
  'nb',
  'nl',
  'pl',
  'pt',
  'pt-BR',
  'ro',
  'ru',
  'sk',
  'sl',
  'sv',
  'th',
  'tr',
  'zh',
  'zh-HK',
  'zh-TW',
]

// Load stripe Elements
export const StripProviderWrapper = ({
  children,
  options,
}: PropsWithChildren & { options?: any }) => {
  const [stripePromise, setStripe] = useState<Stripe | null>(null)
  const router = useRouter()
  const reloadedOnce = useRef<boolean>(false)

  useEffect(() => {
    if (!stripePromise) {
      ;(async () => {
        if (!STRIPE_KEY) {
          return
        }

        try {
          const stripeObject = await loadStripe(STRIPE_KEY, {
            locale:
              router.locale && locales.includes(router.locale)
                ? (router.locale as StripeElementLocale)
                : 'auto',
          })

          if (stripeObject) {
            // @ts-ignore - pure package incorrect types safe to ignore
            setStripe(stripeObject)
          }
        } catch (e) {
          if (!reloadedOnce.current) {
            router.reload()
            reloadedOnce.current = true
          }
          if (e instanceof Error) {
            console.error(e)
            AppManager.toggleSnack(true, e.message, 'error')
          }
        }
      })()
    }
    // close the snackbar if routed from
    if (AppManager.snackbar.open) {
      AppManager.closeSnack()
    }
  }, [stripePromise, router, reloadedOnce, setStripe])

  return (
    <Elements stripe={stripePromise} options={options}>
      {stripePromise ? children : null}
    </Elements>
  )
}

export const StripProvider = memo(StripProviderWrapper)
